import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import {
  formatRatePct,
  KYC_DOC_LABELS,
  useAcceptRateProposal,
  useCurrentAgreement,
  useCurrentRateAgreement,
  useKycStatus,
  useBusinessCategory,
  kycDocTypesFor,
} from '@/lib/data/verification';
import { PageLoading } from '@/ui/Loader';
import { HOST_TERMS_VERSION } from '@/lib/hostTermsVersion.generated';

export const Route = createFileRoute('/verification')({
  component: VerificationPage,
});



function VerificationPage() {
  const { supabase, session } = useAuth();
  const { businessId } = useProperty();
  const userId = session?.user.id ?? null;
  const categoryQuery = useBusinessCategory(supabase, businessId);
  const kycOrder = kycDocTypesFor(categoryQuery.data);

  const agreementQuery = useCurrentAgreement(supabase, userId, businessId);
  const kycQuery = useKycStatus(supabase, userId);
  const rateQuery = useCurrentRateAgreement(supabase, businessId);

  return (
    <section className="flex flex-col gap-lg">
      <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Verification &amp; agreement</h1>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Host Agreement</h2>
        {agreementQuery.isLoading ? <PageLoading compact /> : null}
        {agreementQuery.isError ? <p className="mt-sm text-body text-danger">Could not load your agreement.</p> : null}
        {agreementQuery.data ? (
          <>
            <p className="mt-sm text-body text-ink-2">
              Version {agreementQuery.data.version}, accepted {new Date(agreementQuery.data.accepted_at).toLocaleDateString()}.
            </p>
            {agreementQuery.data.version !== HOST_TERMS_VERSION ? (
              <p className="mt-sm text-body font-semibold text-danger">
                Re-sign needed (current v{HOST_TERMS_VERSION}). Open the NamibiaGo app and accept the updated Host
                Agreement — until then you can't publish new listings or be approved. Your live listings and existing
                bookings are not affected.
              </p>
            ) : null}
            {agreementQuery.data.terms_text ? (
              <details className="mt-md">
                <summary className="cursor-pointer text-body font-semibold text-canyon">Read the accepted text</summary>
                <pre className="mt-sm max-h-96 overflow-auto whitespace-pre-wrap rounded-input border border-hair bg-surface-2 p-md text-caption text-ink">
                  {agreementQuery.data.terms_text}
                </pre>
              </details>
            ) : (
              <p className="mt-sm text-body text-ink-2">The exact text isn't on file for this acceptance.</p>
            )}
          </>
        ) : null}
        {agreementQuery.data === null && !agreementQuery.isLoading ? (
          <p className="mt-sm text-body text-ink-2">
            No agreement on file yet. Accept the Host Agreement (v{HOST_TERMS_VERSION}) in the NamibiaGo app.
          </p>
        ) : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">Commission rate</h2>
        {rateQuery.isLoading ? <PageLoading compact /> : null}
        {rateQuery.data ? <CommissionRateCard agreement={rateQuery.data} businessId={businessId} /> : null}
        {rateQuery.data === null && !rateQuery.isLoading ? (
          <p className="mt-sm text-body text-ink-2">Your business is on the standard rate.</p>
        ) : null}
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <h2 className="text-h3 font-bold text-ink">KYC documents</h2>
        {kycQuery.isLoading ? <PageLoading compact /> : null}
        {kycQuery.isError ? <p className="mt-sm text-body text-danger">Could not load your KYC status.</p> : null}
        {kycQuery.data ? (
          <ul className="mt-sm flex flex-col gap-xs">
            {kycOrder.map((docType) => {
              const row = kycQuery.data!.find((k) => k.doc_type === docType);
              return (
                <li key={docType} className="flex items-center justify-between rounded-input border border-hair px-md py-sm">
                  <span className="text-body text-ink">{KYC_DOC_LABELS[docType]}</span>
                  <span
                    className={`rounded-chip px-sm py-[1px] text-caption font-semibold ${
                      row?.status === 'approved'
                        ? 'bg-tint-success text-success'
                        : row?.status === 'rejected'
                          ? 'bg-surface-2 text-danger'
                          : row
                            ? 'bg-tint-warning text-warning'
                            : 'bg-tint-sand text-ink-2'
                    }`}
                  >
                    {row?.status ?? 'not submitted'}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}
        <p className="mt-md text-caption text-ink-3">Upload or replace KYC documents from the NamibiaGo mobile app.</p>
      </div>

      <div className="rounded-card border border-border bg-surface p-lg shadow-card">
        <div className="flex items-center gap-sm">
          <h2 className="text-h3 font-bold text-ink">Finatic</h2>
          <span className="rounded-chip bg-tint-sand px-sm py-[1px] text-caption font-semibold text-ink-2">Partner</span>
        </div>
        <p className="mt-xs text-body text-ink-2">
          Finatic offers financing for Namibian tourism businesses. NamibiaGo doesn't arrange or guarantee anything
          through this link — it's a referral to their own site.
        </p>
        <a
          href="https://finatic.com.na"
          target="_blank"
          rel="noreferrer noopener"
          className="mt-sm inline-block text-body font-semibold text-canyon underline"
        >
          Learn more at finatic.com.na
        </a>
      </div>
    </section>
  );
}

function CommissionRateCard({
  agreement,
  businessId,
}: {
  agreement: NonNullable<ReturnType<typeof useCurrentRateAgreement>['data']>;
  businessId: string | null;
}) {
  const { supabase } = useAuth();
  const acceptMutation = useAcceptRateProposal(supabase, businessId);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (agreement.status === 'accepted') {
    return (
      <p className="mt-sm text-body text-ink-2">
        You accepted a {formatRatePct(agreement.rate)} rate
        {agreement.accepted_at ? ` on ${new Date(agreement.accepted_at).toLocaleDateString()}` : ''}.
      </p>
    );
  }

  return (
    <div className="mt-sm flex flex-col gap-sm">
      <p className="text-body text-ink-2">A new rate has been proposed for your business:</p>
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-input border border-hair bg-surface-2 p-md text-caption text-ink">
        {agreement.rate_text}
      </pre>
      <label className="flex items-center gap-xs text-body text-ink">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
        I agree to this rate.
      </label>
      <button
        type="button"
        disabled={!agreed || acceptMutation.isPending}
        onClick={() => {
          setError(null);
          acceptMutation.mutate(agreement, {
            onError: (err) => setError(err instanceof Error ? err.message : 'Could not accept this rate.'),
          });
        }}
        className="self-start rounded-button bg-canyon px-md py-xs text-body font-semibold text-white disabled:opacity-50"
      >
        {acceptMutation.isPending ? 'Accepting…' : `Accept ${formatRatePct(agreement.rate)}`}
      </button>
      {error ? <p className="text-body text-danger">{error}</p> : null}
    </div>
  );
}
